import { classifyClientReachability } from '../../../helper/clientEmailStatus';
import { fullName, joinNames } from '../../../helper/clientEmailStatus';
import {
  buildNoEmailText,
  buildNoNotificationText,
  buildNoPortalText,
  getClientsRelevantForReception,
} from '../../clientReceptionWarnings';
import { IHealthCheckInput } from '../types';
import { STATE_IN_PROGRESS } from './legalTransactionState';

/**
 * Regeln zum Bereich „Mandanten" (#1283).
 *
 * Die Erreichbarkeits-Texte kommen aus `clientReceptionWarnings`, damit die
 * Wortwahl an einer Stelle liegt. Anders als dort ist hier aber jede Stufe eine
 * eigene Regel — nur so kann eine Kanzlei z. B. den deaktivierten E-Mail-Empfang
 * ausblenden, ohne die fehlende E-Mail-Adresse mit auszublenden.
 */

/** Kein Mandant zugeordnet. Das Backend erlaubt ein leeres Array. */
export const noneAssigned = ({ legalTransaction }: IHealthCheckInput) =>
  legalTransaction.LegalClientArray.length === 0
    ? [
        'Dem Vorgang ist kein Mandant zugeordnet. Ohne Beteiligte können weder Dokumente bereitgestellt noch Rechnungen zugeordnet werden.',
      ]
    : [];

const reachability = (input: IHealthCheckInput) =>
  classifyClientReachability(
    getClientsRelevantForReception(input.legalTransaction.LegalClientArray)
  );

/** Mandant mit aktiver Erinnerung, aber ohne E-Mail-Adresse. */
export const missingEmail = (input: IHealthCheckInput) => {
  const { noEmail } = reachability(input);
  return noEmail.length > 0 ? [buildNoEmailText(noEmail)] : [];
};

/** E-Mail vorhanden, aber kein Portalzugang. */
export const noPortalAccess = (input: IHealthCheckInput) => {
  const { noPortal } = reachability(input);
  return noPortal.length > 0 ? [buildNoPortalText(noPortal)] : [];
};

/** Portalzugang möglich, aber E-Mail-Empfang deaktiviert. */
export const noNotification = (input: IHealthCheckInput) => {
  const { noNotification: clients } = reachability(input);
  return clients.length > 0 ? [buildNoNotificationText(clients)] : [];
};

/**
 * Beteiligter ohne Rolle. Das Frontend setzt beim Zuordnen still die erste
 * Rolle, das Backend schreibt aber `NULL`, wenn die Rolle unbekannt ist — der
 * Beteiligte sieht dann im Portal nichts.
 */
export const withoutRole = ({ legalTransaction }: IHealthCheckInput) => {
  const withoutRoleClients = legalTransaction.LegalClientArray.filter(
    client => client.idLegalRole === null || client.idLegalRole === undefined
  );

  if (withoutRoleClients.length === 0) {
    return [];
  }

  return [
    withoutRoleClients.length > 1
      ? `Für ${joinNames(
          withoutRoleClients
        )} ist keine Rolle hinterlegt. Ohne Rolle steuert nichts, welche Unterlagen diese Beteiligten im Mandantenportal sehen.`
      : `Für ${fullName(
          withoutRoleClients[0]
        )} ist keine Rolle hinterlegt. Ohne Rolle steuert nichts, welche Unterlagen dieser Beteiligte im Mandantenportal sieht.`,
  ];
};

/**
 * Keine Rolle mit „Zahlungsschuldner". Ohne eine solche Rolle greift die
 * automatische Zuordnung eingehender Rechnungen nicht.
 */
export const noPayerRole = ({ context }: IHealthCheckInput) => {
  // Die Rollen stehen im Vorgangsobjekt selbst (`LegalRoleArray`) und werden
  // nicht separat nachgeladen — ein leeres Array heisst also „geladen und
  // leer", nicht „noch nicht da". Beides in einen Zweig zu werfen hat die Regel
  // faelschlich melden lassen, der Rollenkatalog sei nicht geladen gewesen.
  const roles = context.legalRoles ?? [];

  if (roles.length === 0) {
    return [
      'Für diesen Vorgang sind keine Beteiligtenrollen hinterlegt. Eingehende Rechnungen können daher keinem Beteiligten automatisch zugeordnet werden.',
    ];
  }

  return roles.some(role => role.isPayer)
    ? []
    : [
        'Keine der Rollen dieses Vorgangs ist als Zahlungsschuldner gekennzeichnet. Eingehende Rechnungen können daher keinem Beteiligten automatisch zugeordnet werden.',
      ];
};

/**
 * Kein Mandant soll eine Rechnungserinnerung erhalten. Ersetzt den bisherigen
 * Bestätigungsdialog aus `LegalTransaction/utilities.tsx` — die Bedingung ist
 * unverändert übernommen.
 */
export const noInvoiceReminderRecipient = ({
  legalTransaction,
}: IHealthCheckInput) => {
  const isInProgress =
    legalTransaction.idLegalTransactionState <= STATE_IN_PROGRESS;
  const hasAnyReminder = legalTransaction.LegalClientArray.some(
    client => client.enableInvoiceReminding
  );

  return isInProgress &&
    legalTransaction.LegalClientArray.length > 0 &&
    !hasAnyReminder
    ? [
        'Mindestens ein Mandant sollte eine Rechnungserinnerung erhalten. Derzeit ist die Rechnungserinnerung bei keinem Beteiligten aktiviert.',
      ]
    : [];
};

/**
 * Urkunden liegen im Vorgang, aber kein Beteiligter erhält eine
 * Urkunden-Erinnerung — das Gegenstück zur Rechnungserinnerung.
 */
export const noCertificateReminderRecipient = ({
  legalTransaction,
}: IHealthCheckInput) => {
  if (legalTransaction.CertificateArray.length === 0) {
    return [];
  }

  if (legalTransaction.LegalClientArray.length === 0) {
    return [];
  }

  return legalTransaction.LegalClientArray.some(
    client => client.enableCertificateReminding
  )
    ? []
    : [
        'Im Vorgang liegen Urkunden, aber bei keinem Beteiligten ist die Urkunden-Erinnerung aktiviert. Es wird daher niemand über die Bereitstellung informiert.',
      ];
};
